import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { Icon24ChevronDown } from '@figma/fpl-icons/icon-24-chevron-down';
import { Icon24ChevronUp } from '@figma/fpl-icons/icon-24-chevron-up';
import { ButtonPrimitive } from '../button-primitive/button-primitive.js';
import { looksEqual, doFormatterSafeParse, doFormatterIncrementByNudgeAmount } from '../formatter/formatter.utils.js';
import { Col } from '../stack/stack.js';
import { preventDefault } from '../utils/event.js';
import { button } from './stepper.css.js';

/**
 * Stepper can be composed into an input to provide up/down arrow buttons for incrementing the
 * value. It should be placed last inside of an `Input.Root` component.
 *
 * @see https://fpl.figma.design/components/inputs/scrubbable-input/#stepper
 */
const Stepper = forwardRef(({ value, getStringValue, formatter, onChange, onChangeRestricted }, ref) => {
    const { min, max } = formatter;
    const onIncrement = (e, direction) => {
        // Prevent the click from focusing the button
        e.preventDefault();
        const big = e.shiftKey;
        const valueString = getStringValue();
        const res = doFormatterSafeParse(formatter, valueString, value, 'nudge', e);
        if (res == null)
            return;
        const doUpdate = (val) => doFormatterIncrementByNudgeAmount(formatter, val, direction, { big });
        const update = (val) => doUpdate(val).value;
        if (res.callback) {
            res.callback(update, { commit: true });
            return;
        }
        const newValue = doUpdate(value);
        if (!looksEqual(formatter, value, newValue.value)) {
            onChange?.(newValue.value);
        }
        if (newValue.value !== newValue.preClamped) {
            onChangeRestricted?.(newValue.preClamped, { value: newValue.value });
        }
    };
    const common = { tabIndex: -1, onPointerDown: preventDefault };
    return (jsxs(Col, { "data-fpl-component": "", ref: ref, "aria-hidden": "true", children: [jsx(ButtonPrimitive, { className: button.up, onClick: (e) => onIncrement(e, 1), disabled: max != null && looksEqual(formatter, value, max), ...common, children: jsx(Icon24ChevronUp, {}) }), jsx(ButtonPrimitive, { className: button.down, onClick: (e) => onIncrement(e, -1), disabled: min != null && looksEqual(formatter, value, min), ...common, children: jsx(Icon24ChevronDown, {}) })] }));
});
Stepper.displayName = 'Stepper';

export { Stepper };
//# sourceMappingURL=stepper.js.map
